<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'
import type { Emits, Props } from './type'

defineOptions({
  name: 'AppPageCarousel',
})

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const activeSlide = computed(() => props.slides[props.activeIndex] ?? props.slides[0])
const activeTitle = computed(() => activeSlide.value?.title ?? '')
const activeSubtitleLines = computed(() => activeSlide.value?.subtitleLines ?? [])
const dotProgressStyles = computed<CSSProperties[]>(() => {
  const safeProgress = Math.max(0, Math.min(props.progress, 1))

  return props.slides.map((_, index) => ({
    transform: `scaleX(${index === props.activeIndex ? safeProgress : 0})`,
  }))
})
</script>

<template>
  <div class="app-page-carousel">
    <main class="app-page-carousel__viewport" aria-label="功能介绍轮播">
      <div class="app-page-carousel__slides" :style="slidesStyle">
        <article
          v-for="(slide, index) in slides"
          :key="slide.key"
          class="app-page-carousel__slide"
          :aria-label="`${index + 1}/${slides.length}`"
        >
          <img
            class="app-page-carousel__image"
            :src="slide.image"
            :alt="slide.imageAlt"
            draggable="false"
          />
        </article>
      </div>
    </main>

    <section class="app-page-carousel__copy" aria-live="polite">
      <h2 class="app-page-carousel__title">{{ activeTitle }}</h2>
      <p class="app-page-carousel__subtitle">
        <span
          v-for="line in activeSubtitleLines"
          :key="line"
          class="app-page-carousel__subtitle-line"
        >
          {{ line }}
        </span>
      </p>
    </section>

    <nav class="app-page-carousel__dots" aria-label="轮播分页">
      <button
        v-for="(slide, index) in slides"
        :key="slide.key"
        class="app-page-carousel__dot"
        :class="{ 'is-active': index === activeIndex }"
        type="button"
        :aria-label="`切换到第 ${index + 1} 页`"
        :aria-current="index === activeIndex ? 'true' : undefined"
        @click="emit('dot-click', index)"
      >
        <span
          class="app-page-carousel__dot-progress"
          :style="dotProgressStyles[index]"
          aria-hidden="true"
        />
      </button>
    </nav>
  </div>
</template>

<style scoped>
.app-page-carousel {
  position: absolute;
  inset: 0;
}

.app-page-carousel__viewport {
  position: absolute;
  left: 20px;
  top: 220px;
  width: 360px;
  height: 280px;
  overflow: hidden;
  background: #fff;
}

.app-page-carousel__slides {
  display: flex;
  width: 360px;
  height: 280px;
}

.app-page-carousel__slide {
  display: grid;
  flex: 0 0 360px;
  width: 360px;
  height: 280px;
  padding: 0;
  place-items: center;
}

.app-page-carousel__image {
  display: block;
  width: 360px;
  height: 280px;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.app-page-carousel__copy {
  position: absolute;
  left: 50%;
  top: 500px;
  width: 260px;
  transform: translateX(-50%);
  color: var(--app-page-text);
  text-align: center;
}

.app-page-carousel__title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1.2578;
}

.app-page-carousel__subtitle {
  min-height: 40px;
  margin: 8px 0 0;
  color: var(--app-page-subtext);
  font-size: 15px;
  letter-spacing: 0.08em;
  line-height: 1.358;
}

.app-page-carousel__subtitle-line {
  display: block;
}

.app-page-carousel__dots {
  position: absolute;
  left: 50%;
  top: 605px;
  display: flex;
  width: 76px;
  height: 3px;
  gap: 4px;
  transform: translateX(-50%);
}

.app-page-carousel__dot {
  position: relative;
  width: 16px;
  height: 3px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: var(--app-page-dot);
  border: 0;
  border-radius: 10px;
}

.app-page-carousel__dot-progress {
  position: absolute;
  inset: 0;
  background: var(--app-page-brand);
  transform: scaleX(0);
  transform-origin: left center;
}

@media (prefers-reduced-motion: reduce) {
  .app-page-carousel__slides {
    transition: none !important;
  }
}
</style>
